import type { CSSProperties, HTMLAttributes } from 'react';

export type SkeletonProps = HTMLAttributes<HTMLSpanElement> & {
  shape?: 'line' | 'block' | 'circle';
  width?: number | string;
  height?: number | string;
  /** Convenience: render N stacked lines */
  lines?: number;
  /** Gap between stacked lines */
  gap?: number | string;
};

export function Skeleton({ shape = 'line', width, height, lines, gap, className = '', style, ...rest }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <span className="skeleton-stack" style={{ gap, display: 'grid' }}>
        {Array.from({ length: lines }).map((_, idx) => (
          <Skeleton
            key={idx}
            shape="line"
            width={idx === lines - 1 ? '70%' : width ?? '100%'}
            height={height}
            className={className}
            style={style}
          />
        ))}
      </span>
    );
  }
  const finalStyle: CSSProperties = { ...(style || {}) };
  if (width != null) finalStyle.width = typeof width === 'number' ? `${width}px` : width;
  if (height != null) finalStyle.height = typeof height === 'number' ? `${height}px` : height;
  return <span aria-hidden="true" className={['skeleton', shape, className].filter(Boolean).join(' ')} style={finalStyle} {...rest} />;
}

export function SkeletonText({ lines = 3, ...rest }: { lines?: number } & SkeletonProps) {
  return <Skeleton lines={lines} gap="0.4rem" {...rest} />;
}

export function SkeletonCards({ count = 4 }: { count?: number }) {
  return (
    <div className="grid-stats">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={idx} className="card" style={{ marginBottom: 0 }}>
          <Skeleton shape="line" width="50%" height={12} />
          <div style={{ height: 12 }} />
          <Skeleton shape="line" width="40%" height={28} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-stack" style={{ gap: '0.55rem' }}>
      {Array.from({ length: rows }).map((_, idx) => (
        <Skeleton key={idx} shape="block" height={56} />
      ))}
    </div>
  );
}
